import { deleteCookie, getCookie, sleep } from 'utils/dom'
import { TOKEN_LOCAL_STORAGE_KEY } from 'codegen/fetcher/fetcher.utils'
import type { UserQuery, LogInMutation, RegisterMutation } from 'codegen/generated/user'
import { RegisterOrigin } from 'codegen/types'
import { TEAM_ID_COOKIE_NAME, WORDPRESS_LOGIN_COOKIE_NAME } from 'constants/cookies'
import { getCookieDomain } from 'utils/env'
import { localStore } from 'utils/storage'

const GRAPHQL_URL =
  process.env.NEXT_PUBLIC_GRAPHQL_URL ||
  process.env.VITE_GRAPHQL_URL ||
  process.env.GRAPHQL_URL_SELF_HOSTED

export type AuthOrigin = 'login' | 'registration'

export const RegistrationSource = RegisterOrigin
export type RegistrationSource = RegisterOrigin

type JwtAuthResult = 'OK' | 'HTTP_ERROR' | 'NETWORK_ERROR' | 'RETRY_EXHAUSTED'

const REPORT_JWT_AUTH_OUTCOME_MUTATION = `
  mutation reportJwtAuthOutcome($input: JwtAuthOutcomeInput!) {
    reportJwtAuthOutcome(input: $input) { accepted }
  }
`

function reportJwtAuthOutcome(
  origin: AuthOrigin,
  result: JwtAuthResult,
  httpStatus?: number,
  attempts?: number,
) {
  if (!GRAPHQL_URL || typeof window === 'undefined') {
    return
  }
  const input: Record<string, unknown> = {
    origin: origin.toUpperCase(),
    result,
  }
  if (typeof httpStatus === 'number') {
    input.httpStatus = httpStatus
  }
  if (typeof attempts === 'number') {
    input.attempts = attempts
  }

  // fire-and-forget: never let telemetry block the auth flow
  fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      operationName: 'reportJwtAuthOutcome',
      query: REPORT_JWT_AUTH_OUTCOME_MUTATION,
      variables: { input },
    }),
    keepalive: true,
  }).catch(() => {
    // swallow — telemetry failures must not affect the user
  })
}

// Use the actual generated types instead of creating our own
export type UserAuthResponse =
  | NonNullable<UserQuery['me']>
  | NonNullable<LogInMutation['login']>
  | NonNullable<RegisterMutation['register']>

const deviceIdCookieName = 'cfAmpAnonymousId'
const deviceIdUndefined = '__notSet__'

const CF_URL = process.env.NEXT_PUBLIC_CF_URL || process.env.VITE_CF_URL

export function getDeviceId() {
  if (typeof document === 'undefined') {
    return deviceIdUndefined
  }

  return getCookie(deviceIdCookieName) || deviceIdUndefined
}

export function isOnCFDomain() {
  return Boolean(location.hostname.match(/\.creativefabrica\.com|creativefabrica\.com/))
}

/**
 * Optional hooks for deployment-specific auth behavior that shouldn't live in
 * this shared package. Apps register their own logic at their entry point;
 * an unregistered hook is a no-op.
 */
interface AuthLifecycleHooks {
  /**
   * Fires after an explicit auth event (login, registration, SSO exchange)
   * has persisted a fresh token via `saveToken`. Does NOT fire on the passive
   * token refresh that also goes through `saveToken` on every app startup.
   */
  onTokenSaved?: (token: string, origin: AuthOrigin) => Promise<void> | void
  /**
   * Fires during `logOut`, after the local token has been cleared.
   */
  onLogOut?: () => void
}

let authLifecycleHooks: AuthLifecycleHooks = {}

export function registerAuthLifecycleHooks(hooks: AuthLifecycleHooks) {
  authLifecycleHooks = hooks
}

/**
 * Internal accessor for shared call sites (saveToken, logOut, user-auth-dialog).
 * Exported for cross-package use; not part of the app-facing API.
 */
export function getAuthLifecycleHooks(): AuthLifecycleHooks {
  return authLifecycleHooks
}

/**
 * Persists the JWT for header-based auth. Exported for Studio Desktop, which
 * runs on a CF-domain origin but authenticates with `Authorization: Bearer`
 * from localStorage instead of the cookie bridge `saveToken` would use there.
 */
export function saveTokenToLocalStorage(token: string) {
  localStore.setString(TOKEN_LOCAL_STORAGE_KEY, token)
}

export function clearTokenFromLocalStorage() {
  localStore.remove(TOKEN_LOCAL_STORAGE_KEY)
}

/**
 * This function works out what kind of authentication to use for the token.
 * If you are on a .creativefabrica.com or creativefabrica.com domain, it will
 * save the token to a cookie to enable cookie sharing.
 *
 * If that fails, we fallback to using a token.
 *
 * Since cookie sharing only works on the above domains, we have to use localStorage
 * to save the token on other domains.
 */

export function saveToken(token: string, origin: AuthOrigin) {
  if (isOnCFDomain()) {
    // if saving the cookie fails, we can save to local storage as a fallback.
    return jwtAuth(token, origin).catch(() => {
      saveTokenToLocalStorage(token)
    })
  }

  // Off-cookie domains use the Authorization header. Anything that only makes
  // sense on a fresh auth event lives in the `onTokenSaved` hook at the
  // login/registration/SSO call sites — not here, because saveToken also
  // fires on every startup token refresh.
  saveTokenToLocalStorage(token)
  return Promise.resolve()
}

export async function jwtAuth(token: string, origin: AuthOrigin, attempt = 1) {
  const headers = new Headers()

  headers.append('accept', 'application/json, multipart/mixed')
  headers.append('content-type', 'application/json')

  let res: Response
  try {
    res = await fetch(`${CF_URL}/cfsecure/jwtauth`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        token,
        origin,
      }),
    })
  } catch (error) {
    // network error / CORS / blocked request
    reportJwtAuthOutcome(origin, 'NETWORK_ERROR', undefined, attempt)
    if (attempt > 3) {
      reportJwtAuthOutcome(origin, 'RETRY_EXHAUSTED', undefined, attempt)
      throw error instanceof Error ? error : new Error('Failed to authenticate with JWT')
    }
    await sleep(2000)
    return jwtAuth(token, origin, attempt + 1)
  }

  if (!res.ok) {
    reportJwtAuthOutcome(origin, 'HTTP_ERROR', res.status, attempt)
    if (attempt > 3) {
      reportJwtAuthOutcome(origin, 'RETRY_EXHAUSTED', res.status, attempt)
      throw new Error('Failed to authenticate with JWT')
    }

    await sleep(2000)
    return jwtAuth(token, origin, attempt + 1)
  }

  reportJwtAuthOutcome(origin, 'OK', res.status, attempt)
}

// oxlint-disable-next-line require-await
export async function logOut() {
  clearTokenFromLocalStorage()

  // A hook error must never block logout.
  try {
    authLifecycleHooks.onLogOut?.()
  } catch {}

  try {
    const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN
    deleteCookie(WORDPRESS_LOGIN_COOKIE_NAME, '/', cookieDomain)
    deleteCookie(TEAM_ID_COOKIE_NAME, '/', cookieDomain ? getCookieDomain(cookieDomain) : undefined)
  } catch {}
}
